import { FC } from 'react'

import styled from 'styled-components'

import {
  MetaAdsCampaignDetails,
  MetaAdsCampaignStatus,
} from 'src/Redux/metaAdsDetailsApi'
import { CampaignTrackDetails } from 'src/Components/CampaignListItem/CampaignTrackDetails'
import {
  CampaignPlatformTypeEnum,
  CampaignStatusEnum,
} from 'src/Types/campaigns'
import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import SpotifyIcon from 'src/Assets/Svg/platform-icons/Spotify.svg?react'
import { CampaignStatus } from 'src/Components/CampaignStatus'
import { CampaignPaymentState } from 'src/Redux/artistOrderApi/campaignPayment'
import { LinkButton } from 'src/Components/Buttons/LinkButton'

import { getDurationAndDates } from '../helpers.js'

import { AdsSettings } from './AdsSettings.tsx'

const HeaderContainer = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  align-self: stretch;
`
const HeaderWrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  align-self: stretch;

  @media screen and (min-width: ${parseInt(MAX_MOBILE_WIDTH, 10) + 1}px) {
    width: 320px;
    min-width: 320px;
    max-width: 320px;
    padding: 24px;
    gap: 24px;
    border-radius: 16px;
    box-shadow: ${({ theme }) => theme.shadow.card};
    border: 1px solid ${({ theme }) => theme.color.lightGrey};
  }
`

const SpotifyButton = styled.a`
  display: flex;
  min-width: 32px;
  height: 32px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.lightGrey};
  background: ${({ theme }) => theme.color.white};
  color: ${({ theme }) => theme.color.darkGrey};
`

const StyledCampaignStatus = styled(CampaignStatus)``

const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  align-self: stretch;
`

const LinkToLanding = styled(LinkButton)`
  align-self: stretch;
`

const StatusContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  padding-left: 84px;
`
const StatusText = styled.div`
  color: ${({ theme }) => theme.color.darkGrey};
  font-family: 'Segoe UI';
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%;
`

type CampaignHeaderProps = MetaAdsCampaignDetails & {
  paymentState?: CampaignPaymentState | null
}

const CampaignHeader: FC<CampaignHeaderProps> = (props) => {
  const {
    ownerName,
    resourceName,
    coverUrl,
    status,
    paymentState,
    landingUrl,
    similarArtistNames,
    resourceSpotifyUrl,
    countries,
    genres,
    languages,
    startDate,
    endDate,
  } = props

  const hasSettings = status !== MetaAdsCampaignStatus.APPROVED
  const hasDate = Boolean(startDate && endDate)

  const { duration, dates } = getDurationAndDates(startDate, endDate)

  return (
    <HeaderWrapper>
      <HeaderContainer>
        <CampaignTrackDetails
          artistName={ownerName}
          trackName={resourceName}
          trackImage={coverUrl}
          platform={CampaignPlatformTypeEnum.META}
        />
        {resourceSpotifyUrl && (
          <SpotifyButton
            href={resourceSpotifyUrl}
            target='_blank'
            rel='noreferrer'
          >
            <SpotifyIcon width={16} height={16} />
          </SpotifyButton>
        )}
      </HeaderContainer>

      <StatusContainer>
        <StyledCampaignStatus
          status={status as unknown as CampaignStatusEnum}
          paymentState={paymentState}
        />
        {status === MetaAdsCampaignStatus.SCHEDULED && (
          <StatusText>Will start within 24 - 72h</StatusText>
        )}
        {hasDate && (
          <StatusText>
            <span>{duration}</span>
            <br />
            <span>{dates}</span>
          </StatusText>
        )}
      </StatusContainer>

      <ButtonsWrapper>
        {landingUrl ? (
          <LinkToLanding href={landingUrl}>View smart link</LinkToLanding>
        ) : null}
        {hasSettings && (
          <AdsSettings
            genres={genres}
            languages={languages}
            similarArtistNames={similarArtistNames}
            countries={countries}
          />
        )}
      </ButtonsWrapper>
    </HeaderWrapper>
  )
}

export default CampaignHeader
